import { PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Disclosure } from "@headlessui/react";
import AnimationOnScroll from "react-animate-on-scroll";
import "animate.css/animate.min.css";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFDownloadLink,
  Font,
  Image,
  Svg,
  Path,
} from "@react-pdf/renderer";
import BlinkIntervalsChart from "../components/BlinkIntervalsChart";
import { ReactComponent as Chevron } from "../assets/icons/Chevron.svg";
import { ReactComponent as Caret } from "../assets/icons/Caret.svg";
import { ReactComponent as StressLow } from "../assets/icons/StressLow.svg";
import { ReactComponent as StressNormal } from "../assets/icons/StressNormal.svg";
import { ReactComponent as StressElevated } from "../assets/icons/StressElevated.svg";
import { ReactComponent as StressHigh } from "../assets/icons/StressHigh.svg";
import { ReactComponent as StressVeryHigh } from "../assets/icons/StressVeryHigh.svg";
import { ReactComponent as Download } from "../assets/icons/Download.svg";
import Cover from "../assets/images/Cover.png";
import TopLogo from "../assets/images/top-logo.png";
import InfoCircle from "../assets/images/info-circle.png";
import StressLoww from "../assets/images/StressLow.png";
import BackArrow from "../assets/images/back-arrow.svg";

Font.register({
  family: "Poppins",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLDz8V1tvFP-KUEg.ttf",
      fontWeight: "light",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiEyp8kv8JHgFVrFJDUc1NECPY.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLGT9V1tvFP-KUEg.ttf",
      fontWeight: "medium",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLEj6V1tvFP-KUEg.ttf",
      fontWeight: "semibold",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLCz7V1tvFP-KUEg.ttf",
      fontWeight: "bold",
    },
  ],
});

const ranges = {
  DryEyes: [
    {
      point: 0,
      color: "#DD3923",
      category: "High Risk",
      description:
        "A score in this range indicates a significantly high risk of dry eyes. Individuals may require immediate attention to improve eye hydration and comfort. Consistent management strategies, such as hydration, environmental modifications, and professional consultation, are highly recommended.",
    },
    {
      point: 25,
      color: "#F18233",
      category: "Moderate Risk",
      description:
        "This range suggests an increased likelihood of dry eyes, particularly in certain environments or activities. Preventive measures like maintaining hydration, following proper eye care routines, and reducing strain can help manage the risk effectively.",
    },
    {
      point: 50,
      color: "#FAC02B",
      category: "Mild Risk",
      description:
        "Individuals in this range have a low risk of dry eyes but may occasionally experience mild discomfort under specific conditions. Simple lifestyle adjustments and regular eye care practices can help maintain optimal ocular hydration.",
    },
    {
      point: 75,
      color: "#64BD40",
      category: "No Risk",
      description:
        "A score in this range indicates well-hydrated and healthy eyes with no significant risk of dryness. Maintaining good eye care habits and a balanced lifestyle can support long-term eye health.",
    },
    {
      point: 100,
      color: "#64BD40",
      category: "No Risk",
      description:
        "A score in this range indicates well-hydrated and healthy eyes with no significant risk of dryness. Maintaining good eye care habits and a balanced lifestyle can support long-term eye health.",
    },
  ],
  BlinkIndex: [
    {
      point: 0,
      color: "#DD3923",
      category: "High Risk",
      description:
        "A score in this range suggests a significantly high risk of dry eyes, often due to excessive blinking caused by tear film instability. Individuals may need to take corrective measures such as optimizing screen habits, improving hydration, and consulting an eye specialist.",
    },
    {
      point: 25,
      color: "#F18233",
      category: "Moderate Risk",
      description:
        "Individuals in this range show an increased blink rate, indicating a moderate risk of dry eyes. Environmental factors like prolonged screen time or air-conditioned surroundings could be contributing to this deviation. Adopting simple eye care practices can help restore balance.",
    },
    {
      point: 50,
      color: "#FAC02B",
      category: "Mild Risk",
      description:
        "A score in this range suggests a mostly stable blink rate with slight variations. The risk of dry eyes is low, but occasional factors like fatigue or temporary strain may cause mild fluctuations. Maintaining regular blinking habits and hydration can help sustain eye comfort.",
    },
    {
      point: 75,
      color: "#64BD40",
      category: "No Risk",
      description:
        "A score in this range indicates a healthy and stable blink rate, signifying no significant risk of dry eyes. Maintaining good screen hygiene, staying hydrated, and following natural blinking patterns will help preserve optimal eye health.",
    },
    {
      point: 100,
      color: "#64BD40",
      category: "No Risk",
      description:
        "A score in this range indicates a healthy and stable blink rate, signifying no significant risk of dry eyes. Maintaining good screen hygiene, staying hydrated, and following natural blinking patterns will help preserve optimal eye health.",
    },
  ],
  BlinkInterval: [
    {
      point: 0,
      color: "#A20303",
      category: "Very High",
      description:
        "Extremely frequent blinking, often indicating significant tear film instability and severe dry eye risk. Immediate attention to eye hydration and care is recommended.",
    },
    {
      point: 1,
      color: "#DD3923",
      category: "High",
      description:
        "Faster than normal blinking, suggesting a high likelihood of dry eyes. Could be due to environmental factors, excessive screen use, or underlying eye conditions.",
    },
    {
      point: 2,
      color: "#FAC02B",
      category: "Slightly Increased",
      description:
        "Moderately frequent blinking, potentially signaling early signs of dryness or strain. Maintaining good eye care practices can help restore balance.",
    },
    {
      point: 3,
      color: "#64BD40",
      category: "Normal",
      description:
        "Ideal blink interval, indicating a stable tear film and well-hydrated eyes. No significant risk of dry eyes, but maintaining regular eye care is beneficial.",
    },
    {
      point: 4,
      color: "#FAC02B",
      category: "Slightly Reduced",
      description:
        "Less frequent blinking than normal, which may suggest minor tear film irregularities. Awareness of blinking habits and hydration can help maintain eye comfort",
    },
    {
      point: 5,
      color: "#DD3923",
      category: "Low",
      description:
        "Extended blink intervals could indicate reduced tear production or prolonged focus, leading to potential discomfort over time. Conscious blinking and hydration may be helpful.",
    },
    {
      point: 6,
      color: "#A20303",
      category: "Very Low",
      description:
        "Significantly infrequent blinking, often linked to prolonged screen exposure or reduced tear secretion. Higher risk of eye strain and dryness, requiring proactive eye care.",
    },
    {
      point: 7,
      color: "#A20303",
      category: "Very Low",
      description:
        "Significantly infrequent blinking, often linked to prolonged screen exposure or reduced tear secretion. Higher risk of eye strain and dryness, requiring proactive eye care.",
    },
  ],
  OSDI: [
    {
      point: 0,
      color: "#64BD40",
      category: "Normal",
      description:
        "No significant symptoms of ocular surface disease. Individuals in this range typically experience stable tear film and good ocular comfort, with no noticeable visual disturbances or dryness-related discomfort in daily activities.",
    },
    {
      point: 13,
      color: "#FAC02B",
      category: "Mild",
      description:
        "Occasional or mild symptoms of ocular surface discomfort, such as intermittent dryness or slight irritation, particularly in challenging environments like air-conditioned spaces or prolonged screen use. Symptoms may be manageable with basic eye care practices.",
    },
    {
      point: 23,
      color: "#F18233",
      category: "Moderate",
      description:
        "More frequent or noticeable symptoms impacting daily activities, including increased dryness, sensitivity, and potential visual disturbances. Individuals in this range may require interventions such as artificial tears, blinking exercises, or environmental modifications.",
    },
    {
      point: 33,
      color: "#A20303",
      category: "Severe",
      description:
        "Persistent and significant ocular discomfort, with symptoms potentially affecting vision and quality of life. Individuals in this category may struggle with prolonged screen use, reading, or exposure to wind and air conditioning. Medical consultation and targeted treatments are often necessary.",
    },
    {
      point: 100,
      color: "#A20303",
      category: "Severe",
      description:
        "Persistent and significant ocular discomfort, with symptoms potentially affecting vision and quality of life. Individuals in this category may struggle with prolonged screen use, reading, or exposure to wind and air conditioning. Medical consultation and targeted treatments are often necessary.",
    },
  ],
  StressLevel: [
    {
      point: 0,
      color: "#3CA312",
      category: "Low",
      icon: <StressLow height={40} width={40} />,
    },
    {
      point: 0.5,
      color: "#64BD40",
      category: "Normal",
      icon: <StressNormal height={40} width={40} />,
    },
    {
      point: 1.5,
      color: "#FAC02B",
      category: "Elevated",
      icon: <StressElevated height={40} width={40} />,
    },
    {
      point: 5,
      color: "#DD3923",
      category: "High",
      icon: <StressHigh height={40} width={40} />,
    },
    {
      point: 9,
      color: "#A20303",
      category: "Very High",
      icon: <StressVeryHigh height={40} width={40} />,
    },
    {
      point: 10,
      color: "#A20303",
      category: "Very High",
      icon: <StressVeryHigh height={40} width={40} />,
    },
  ],
};

const Range = ({ ranges, selectedIndex, className = "" }) => {
  selectedIndex -= Number(ranges.length - 1 === selectedIndex);
  return (
    <div
      className={`grow relative grid grid-flow-col auto-cols-fr ${className}`}
    >
      {ranges.slice(0, -1).map((rangeItem, index, list) => (
        <div key={`range-${index}`} className="flex flex-col justify-end">
          {index === selectedIndex && (
            <Caret
              className="self-center shrink-0 w-2 h-1"
              style={{ color: ranges[selectedIndex].color }}
            />
          )}
          <div
            className="my-1 h-1"
            style={{
              backgroundColor:
                index === selectedIndex
                  ? ranges[selectedIndex].color
                  : "#c0c0c069",
              borderTopLeftRadius: index === 0 ? 9999 : undefined,
              borderBottomLeftRadius: index === 0 ? 9999 : undefined,
              borderTopRightRadius:
                index === list.length - 1 ? 9999 : undefined,
              borderBottomRightRadius:
                index === list.length - 1 ? 9999 : undefined,
            }}
          />
          <p className="text-primary/70 text-xxs">{rangeItem.point}</p>
        </div>
      ))}
      <p className="absolute bottom-0 right-0 text-primary/70 text-xxs">
        {ranges[ranges.length - 1].point}
      </p>
    </div>
  );
};

const ListItemContainer = ({
  disabled = false,
  defaultExpanded = false,
  label,
  desc,
  className = "",
  ...props
}) => {
  return (
    <Disclosure
      defaultOpen={defaultExpanded}
      as="div"
      className="px-4 py-4 rounded-2xl border border-secondary/50 bg-[#fefefe] text-primary "
    >
      <Disclosure.Button as="div" disabled={disabled}>
        {({ open }) => (
          <>
            <div className="flex items-start justify-between space-x-1">
              <h2 className="text-[13px] font-medium">{label}</h2>
              <Chevron
                className={`h-3 w-3 opacity-70 transition-transform duration-300 ${
                  open ? "rotate-180" : "rotate-0"
                }`}
              />
            </div>
            {props?.children}
          </>
        )}
      </Disclosure.Button>
      <Disclosure.Panel
        as="p"
        className="mt-1.5 text-xxs font-light leading-tight whitespace-pre-line"
      >
        {desc}
      </Disclosure.Panel>
    </Disclosure>
  );
};

const PDFReport = ({
  name,
  timestamp = new Date(),
  dryEyeScore,
  osdi,
  stressLevel,
  blinkIndex,
  blinkIntervals,
  blinkCount,
  leftEyeImage,
  rightEyeImage,
}) => {
  const styles = StyleSheet.create({
    page: {
      backgroundColor: "#fff",
      flexDirection: "column",
      alignItems: "stretch",
      justifyContent: "space-between",
    },
    header: {
      paddingVertical: 15,
      paddingHorizontal: 30,
      backgroundColor: "#fff",
      borderBottomWidth: 0.5,
      borderBottomColor: "#D5D5D5",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    contentContainer: { flex: 1, paddingVertical: 15, paddingHorizontal: 30 },
    rowContainer: { flexDirection: "row", justifyContent: "space-between" },
    largeText: { fontFamily: "Poppins", fontSize: 15, fontWeight: "bold" },
    normalText: { fontFamily: "Poppins", fontSize: 10, fontWeight: "semibold" },
    smallText: { fontFamily: "Poppins", fontSize: 10 },
    footer: {
      paddingVertical: 15,
      paddingHorizontal: 10,
      backgroundColor: "#0E6EA5",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 50,
    },
  });

  const Range = ({ ranges, selectedIndex, containerStyle }) => {
    selectedIndex -= Number(ranges.length - 1 === selectedIndex);

    return (
      <View
        style={[
          { width: "42.5%", alignSelf: "center", flexDirection: "row" },
          containerStyle,
        ]}
      >
        {ranges.slice(0, -1).map((rangeItem, index, list) => {
          return (
            <View
              key={`range-${index}`}
              style={{ flex: 1, position: "relative" }}
            >
              {index === selectedIndex && (
                <View style={{ position: "absolute", top: -10, left: 25 }}>
                  <Svg width={12} height={10} viewBox="0 0 10 6">
                    <Path
                      d="M0 0 L5 6 L10 0 Z"
                      fill={ranges[selectedIndex].color}
                    />
                  </Svg>
                </View>
              )}
              <View
                style={{
                  height: 5,
                  backgroundColor:
                    index === selectedIndex
                      ? ranges[selectedIndex].color
                      : "#c0c0c069",
                  borderTopLeftRadius: index > 0 ? 0 : 20,
                  borderBottomLeftRadius: index > 0 ? 0 : 20,
                  borderTopRightRadius: index < list.length - 1 ? 0 : 20,
                  borderBottomRightRadius: index < list.length - 1 ? 0 : 20,
                }}
              />
              <Text style={{ fontSize: 8, color: "#707070" }}>
                {rangeItem.point}
              </Text>
            </View>
          );
        })}
        <Text
          style={{
            fontFamily: "Poppins",
            fontSize: 8,
            lineHeight: 1.5,
            color: "#6E7A80",
            maxLines: 1,
            textOverflow: "ellipsis",
            marginTop: 4,
            marginRight: 2,
          }}
        >
          {ranges[ranges.length - 1].point}
        </Text>
      </View>
    );
  };
  return (
    <Document
      author="CarePlix"
      title="VisionAI Scan Report | CarePlix"
      subject="Report generated from VisionAI Scan"
    >
      <Page wrap={false}>
        <View
          style={{
            position: "relative",
            height: "100%",
            width: "100%",
            padding: 56,
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "space-between",
          }}
        >
          <Image
            source={Cover}
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              objectFit: "cover",
            }}
          />
          <View>
            <Text
              style={{
                fontFamily: "Poppins",
                fontSize: 40,
                lineHeight: 1.2,
                fontWeight: "semibold",
                color: "#fff",
                textAlign: "center",
                marginTop: 135,
              }}
            >
              {"Dry Eye\nAssesment Report"}
            </Text>
          </View>
        </View>
      </Page>
      <Page style={styles.page}>
        <View fixed style={styles.header}>
          <Image src={TopLogo} style={{ width: 140, height: 50 }} />
          <View style={{ alignItems: "flex-end" }}>
            {[
              {
                label: "Assessment Date",
                value: timestamp.toLocaleDateString(undefined, {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                }),
              },
              {
                label: "Assessment Time",
                value: timestamp.toLocaleTimeString(undefined, {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                }),
              },
            ].map(({ label, value }) => (
              <Text
                key={label.toLowerCase().replaceAll(" ", "-")}
                style={{ fontFamily: "Poppins", fontSize: 10, lineHeight: 1.5 }}
              >
                <Text style={{ color: "#000000", fontWeight: "semibold" }}>
                  {label}:{" "}
                </Text>
                <Text style={{ color: "#000000", fontWeight: "normal" }}>
                  {value}
                </Text>
              </Text>
            ))}
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Text
                style={{
                  fontFamily: "Poppins",
                  fontWeight: "semibold",
                  fontSize: 10,
                  lineHeight: 1.5,
                  marginRight: 3,
                }}
              >
                Name:
              </Text>
              <Text
                style={{ color: "#000", fontSize: 10, fontWeight: "normal" }}
              >
                {name}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.contentContainer}>
          <Text
            style={{
              fontFamily: "Poppins",
              fontWeight: "bold",
              color: "#000",
              fontSize: 16,
              marginBottom: 10,
            }}
          >
            Assessment Result
          </Text>
          <View>
            {[
              {
                label: "Dry Eye Risk Indicator",
                value: dryEyeScore,
                unit: "%",
                ranges: ranges.DryEyes,
                caregoryVisible: true,
              },
              {
                label: "Blink Index (BI)",
                value: blinkIndex,
                unit: "%",
                ranges: ranges.BlinkIndex,
              },
              {
                label: "Blink Interval",
                value: blinkIntervals?.avg ?? NaN,
                unit: "s",
                ranges: ranges.BlinkInterval,
              },
              {
                label: "Ocular Surface Disease Index (OSDI)",
                value: osdi?.toFixed?.(2) ?? NaN,
                unit: "",
                ranges: ranges.OSDI,
              },
            ].map((item, index) => {
              const value = parseFloat(item.value);
              const selectedRangeIndex = isNaN(value)
                ? -1
                : item.ranges.reduce(
                    (p, c, ci) => (value > c.point ? ci : p),
                    0
                  );
              //@ts-ignore
              const { color, category, description } = isNaN(value)
                ? {}
                : item.ranges[selectedRangeIndex];
              return (
                <View
                  key={item.label.toLowerCase().replaceAll(" ", "-")}
                  style={{
                    borderWidth: 1,
                    borderColor: "#C2DAE8",
                    borderRadius: 10,
                    marginBottom: 5,
                  }}
                >
                  <View
                    style={{
                      flexDirection: "row",
                      gap: 10,
                      marginBottom: 5,
                      padding: 10,
                    }}
                  >
                    <View style={{ flex: 1, gap: 0 }}>
                      <Text
                        style={{
                          fontFamily: "Poppins",
                          fontSize: 12,
                          lineHeight: 1.2,
                          fontWeight: "bold",
                          color: "#0D212C",
                          marginBottom: 6,
                        }}
                      >
                        {item.label}
                      </Text>
                      <View style={{ display: "flex", flexDirection: "row" }}>
                        <View
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "flex-end",
                            marginRight: 10,
                          }}
                        >
                          <Text
                            style={{
                              fontSize: 14,
                              fontFamily: "Poppins",
                              fontWeight: "bold",
                              color: color,
                            }}
                          >
                            {isNaN(value) ? "--" : value}
                          </Text>
                          <Text
                            style={{
                              fontSize: 8,
                              color: "#000",
                              marginBottom: 5,
                              marginLeft: 3,
                            }}
                          >
                            {item.unit}
                          </Text>
                        </View>
                        <View
                          style={{
                            backgroundColor:
                              typeof color === "string"
                                ? `${color}20`
                                : undefined,
                            borderRadius: 5,
                            height: 20,
                            paddingHorizontal: 10,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Text
                            style={{
                              fontSize: 9,
                              color: color,
                              fontFamily: "Poppins",
                              fontWeight: "medium",
                            }}
                          >
                            {category}
                          </Text>
                        </View>
                      </View>
                    </View>
                    <View
                      style={{ alignSelf: "center", gap: 10, width: "47.5%" }}
                    >
                      <Range
                        containerStyle={{ width: "100%", alignSelf: "stretch" }}
                        ranges={item.ranges}
                        selectedIndex={selectedRangeIndex}
                      />
                    </View>
                  </View>
                  <View
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      backgroundColor: "rgba(14,110,165,0.1)",
                      borderBottomLeftRadius: 10,
                      borderBottomRightRadius: 10,
                      paddingHorizontal: 10,
                      paddingVertical: 6,
                    }}
                  >
                    <Image
                      src={InfoCircle}
                      style={{ width: 15, height: 15, marginRight: 5 }}
                    />
                    <Text
                      style={{
                        fontSize: 8,
                        fontFamily: "Poppins",
                        color: "rgba(0,0,0,0.7)",
                        width: "97%",
                        lineHeight: 1.1,
                      }}
                    >
                      {description}
                    </Text>
                  </View>
                </View>
              );
            })}
          </View>
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <View
              style={{ width: "65%", display: "flex", flexDirection: "row" }}
            >
              <View>
                {rightEyeImage?.length > 0 && (
                  <Image
                    src={rightEyeImage}
                    style={{ width: 160, height: 80 }}
                  />
                )}
                <View
                  style={{
                    backgroundColor: "#161616",
                    padding: 5,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Text
                    style={{
                      textAlign: "center",
                      fontSize: 10,
                      color: "#000",
                      fontFamily: "Poppins",
                    }}
                  >
                    Right Eye
                  </Text>
                </View>
              </View>
              <View style={{ marginLeft: 10 }}>
                {leftEyeImage?.length > 0 && (
                  <Image
                    src={leftEyeImage}
                    style={{ width: 160, height: 80 }}
                  />
                )}
                <View
                  style={{
                    backgroundColor: "#161616",
                    padding: 5,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Text
                    style={{
                      textAlign: "center",
                      fontSize: 10,
                      color: "#000",
                      fontFamily: "Poppins",
                    }}
                  >
                    Left Eye
                  </Text>
                </View>
              </View>
            </View>
            <View style={{ width: "37%" }}>
              <Text
                style={{
                  fontSize: 12,
                  fontFamily: "Poppins",
                  fontWeight: "semibold",
                  marginBottom: 5,
                }}
              >
                Blink Intervals
              </Text>
              <View
                style={{
                  borderWidth: 1,
                  borderColor: "#CFE2ED",
                  borderRadius: 10,
                }}
              >
                {[
                  { label: "Blinks Count", value: blinkCount ?? "--" },
                  {
                    label: "Avg. Interval",
                    value: `${blinkIntervals?.avg ?? "--"} sec`,
                  },
                  {
                    label: "Max Interval",
                    value: `${blinkIntervals?.max ?? "--"} sec`,
                  },
                  {
                    label: "Min Interval",
                    value: `${blinkIntervals?.min ?? "--"} sec`,
                  },
                ].map((d, idx) => (
                  <View
                    key={idx}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      backgroundColor:
                        idx % 2 === 0 ? "#F4F5F9" : "transparent",
                      borderTopRightRadius: idx === 0 ? 10 : 0,
                      borderTopLeftRadius: idx === 0 ? 10 : 0,
                      paddingHorizontal: 10,
                      paddingVertical: 3,
                    }}
                  >
                    <Text style={{ fontSize: 10, fontFamily: "Poppins" }}>
                      {d.label}:
                    </Text>
                    <Text style={{ fontSize: 10, fontFamily: "Poppins" }}>
                      {d.value}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
          <View>
            {(() => {
              const selectedRange = ranges.StressLevel.reduce((p, c) =>
                stressLevel > c.point ? c : p
              );
              return (
                <View
                  style={{
                    width: "62%",
                    backgroundColor: "#F3F7FA",
                    borderWidth: 1,
                    borderColor: "#CFE2ED",
                    borderRadius: 10,
                    padding: "5px 10px",
                  }}
                >
                  <View
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                    }}
                  >
                    <View style={{ display: "flex", flexDirection: "row" }}>
                      <Image
                        src={StressLoww}
                        style={{ width: 30, height: 30, marginRight: 5 }}
                      />
                      <View>
                        <Text
                          style={{
                            fontSize: 12,
                            fontWeight: "medium",
                            fontFamily: "Poppins",
                          }}
                        >
                          Stress Level
                        </Text>
                        <Text
                          style={{
                            fontSize: 10,
                            fontFamily: "Poppins",
                            color: selectedRange.color,
                          }}
                        >
                          {isNaN(stressLevel) ? "--" : selectedRange.category}
                        </Text>
                      </View>
                    </View>
                    <View
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 14,
                          fontWeight: "bold",
                          fontFamily: "Poppins",
                          color: selectedRange.color,
                          textAlign: "right",
                        }}
                      >
                        {isNaN(stressLevel) ? "--" : stressLevel}
                      </Text>
                      <Text
                        style={{
                          fontSize: 10,
                          fontFamily: "Poppins",
                          textAlign: "right",
                        }}
                      >
                        Stress Index(SI)
                      </Text>
                    </View>
                  </View>
                  <View>
                    <Text
                      style={{
                        fontFamily: "Poppins",
                        fontWeight: "medium",
                        fontSize: 10,
                      }}
                    >
                      Your Stress Level is {selectedRange.category}
                    </Text>
                    <Text style={{ fontFamily: "Poppins", fontSize: 8 }}>
                      The body's reaction to a challenge or demand. There are 5
                      levels of stress.
                    </Text>
                  </View>
                </View>
              );
            })()}
          </View>
          <View style={{ padding: 0, marginTop: 10 }}>
            <Text style={[styles.normalText, { fontWeight: "bold" }]}>
              Disclaimer
            </Text>
            <Text
              style={[
                styles.smallText,
                {
                  marginTop: 0,
                  textAlign: "justify",
                  fontSize: 8,
                  lineHeight: 1.1,
                },
              ]}
            >
              For Investigational Use Only. Careplix is not a substitute for the
              clinical judgment of a health care professional. Careplix is
              intended to improve your awareness of general wellness. Careplix
              does not diagnose, treat, mitigate or prevent any disease,
              symp-tom, disorder or abnormal physical state. Consult with a
              health care professional or emergency services if you believe you
              may have a medical issue.
            </Text>
          </View>
        </View>

        <View fixed style={styles.footer}>
          <View
            style={{
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text
              style={{
                fontFamily: "Poppins",
                fontSize: 10,
                lineHeight: 1.5,
                fontWeight: "normal",
                color: "#fff",
              }}
            >
              {`Report Generated from ${
                window.location.hostname
              } on ${new Date().toISOString()}`}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const VisionResult = () => {
  const { state } = useLocation();
  const leftEyeCanvasRef = useRef(null);
  const rightEyeCanvasRef = useRef(null);
  const [leftEyeImageBase64, setLeftEyeImageBase64] = useState("");
  const [rightEyeImageBase64, setRightEyeImageBase64] = useState("");

  const { blinkCount, blinkIntervals } = useMemo(() => {
    if (!(state?.eyeBlinkData?.length > 0)) return {};
    let blinks = 0;
    let blinkIntervalList = [];
    let eyeTriggerStart = 0;
    let eyeTrigger = true;
    state.eyeBlinkData.forEach(({ confidence, timestamp }) => {
      if (confidence > 0.4) eyeTrigger = true;
      else if (eyeTrigger) {
        const duration = Math.abs(timestamp - eyeTriggerStart) / 1000;
        if (duration >= 0.5) {
          blinkIntervalList.push(duration);
          blinks++;
        }
        eyeTriggerStart = timestamp;
        eyeTrigger = false;
      }
    });
    if (blinks === 0 || blinkIntervalList.length === 0) return {};
    return {
      blinkCount: blinks,
      blinkIntervals: {
        avg: (
          blinkIntervalList.reduce((s, v) => s + v, 0) /
          blinkIntervalList.length
        ).toFixed(1),
        max: Math.max(...blinkIntervalList).toFixed(1),
        min: Math.min(...blinkIntervalList).toFixed(1),
      },
    };
  }, [state?.eyeBlinkData]);

  const blinkIndex = useMemo(
    () =>
      typeof blinkCount === "number"
        ? blinkCount > 8
          ? Math.min(100 - (blinkCount - 15) * 5, 100)
          : Math.max(100 - (9 - blinkCount) * 20, 0)
        : NaN,
    [blinkCount]
  );

  const dryEyeScore = useMemo(() => {
    let score = 0;
    let weight = 0;
    if (typeof state?.osdi === "number") {
      score += state.osdi < 50 ? (100 - state.osdi * 2) * 4 : 0;
      weight += 4;
    }
    if (Number.isFinite(blinkIndex)) {
      score += blinkIndex * 3;
      weight += 3;
    }
    if (
      typeof state?.result?.metadata?.heart_scores?.stress_index === "number"
    ) {
      score += 100 - state.result.metadata.heart_scores.stress_index * 10;
      weight += 1;
    }
    return Math.min(Math.max(Math.round(score / weight), 0), 100);
  }, [blinkIndex, state]);

  useEffect(() => {
    const leftEyeImageData = state?.leftEyeImageData;
    if (
      leftEyeCanvasRef.current &&
      leftEyeImageData !== undefined &&
      leftEyeImageData.data?.length > 0
    ) {
      leftEyeCanvasRef.current.height = leftEyeImageData.height;
      leftEyeCanvasRef.current.width = leftEyeImageData.width;
      const lEctx = leftEyeCanvasRef.current.getContext("2d");
      lEctx?.putImageData?.(leftEyeImageData, 0, 0);
      const base64 = leftEyeCanvasRef.current.toDataURL("image/png");
      setLeftEyeImageBase64(base64);
    }
    const rightEyeImageData = state?.rightEyeImageData;
    if (
      rightEyeCanvasRef.current &&
      rightEyeImageData !== undefined &&
      rightEyeImageData.data?.length > 0
    ) {
      rightEyeCanvasRef.current.height = rightEyeImageData.height;
      rightEyeCanvasRef.current.width = rightEyeImageData.width;
      const rEctx = rightEyeCanvasRef.current.getContext("2d");
      rEctx?.putImageData?.(rightEyeImageData, 0, 0);
      const base64 = rightEyeCanvasRef.current.toDataURL("image/png");
      setRightEyeImageBase64(base64);
    }
  }, [state?.leftEyeImageData, state?.rightEyeImageData]);

  const reportPDF = useMemo(
    () => (
      <PDFReport
        name={state?.name}
        timestamp={
          new Date(state?.result?.entry_time?.replace?.(" ", "T") ?? 0)
        }
        dryEyeScore={dryEyeScore}
        osdi={state?.osdi}
        stressLevel={parseFloat(
          state?.result?.metadata.heart_scores.stress_index
        )}
        blinkIndex={blinkIndex}
        blinkIntervals={blinkIntervals}
        blinkCount={blinkCount}
        leftEyeImage={leftEyeImageBase64}
        rightEyeImage={rightEyeImageBase64}
      />
    ),
    [
      dryEyeScore,
      state,
      blinkIndex,
      blinkIntervals,
      blinkCount,
      leftEyeImageBase64,
      rightEyeImageBase64,
    ]
  );

  return state?.result?.scan_id?.length > 0 ? (
    <main className="pt-4 space-y-8">
      <div className="flex px-6 flex-row items-center relative z-50 tab-screen">
        <Link
          to="https://gitex-demo.careplix.com/UnityHealthExperienceCenter"
          className="w-9 h-9 bg-[#2D2D2D] rounded-full absolute left-6 flex flex-row justify-center items-center"
        >
          <img src={BackArrow} alt="" />
        </Link>
        <p className="text-xl lg:text-2xl md:text-2xl font-medium ml-14">
          Vision <span className="text-secondary">AI</span>
        </p>
      </div>
      <h4 className="text-center text-base font-semibold relative z-50 px-6">
        Assessment Result
      </h4>
      <div className="circle-blur w-[75%] h-64 mx-auto absolute top-10 left-0 right-0 blur-[65px]"></div>
      <div className="px-6 bg-white w-full pt-7 pb-14 relative z-50">
        <div className="tab-screen">
          <section className="grid grid-cols-2 gap-4 relative z-50">
            {[
              {
                label: "Dry Eye Risk Indicator",
                value: dryEyeScore,
                unit: "%",
                ranges: ranges.DryEyes,
                caregoryVisible: true,
              },
              {
                label: "Blink Index (BI)",
                value: blinkIndex,
                unit: "%",
                ranges: ranges.BlinkIndex,
              },
              {
                label: "Blink Interval",
                value: blinkIntervals?.avg ?? NaN,
                unit: "s",
                ranges: ranges.BlinkInterval,
              },
              {
                label: "Ocular Surface Disease Index (OSDI)",
                value: state?.osdi?.toFixed?.(2) ?? NaN,
                unit: "",
                ranges: ranges.OSDI,
              },
            ].map((item, index) => {
              const value = parseFloat(item.value);
              const selectedRangeIndex = isNaN(value)
                ? -1
                : item.ranges.reduce(
                    (p, c, ci) => (value > c.point ? ci : p),
                    0
                  );
              //@ts-ignore
              const { color, category, description } = isNaN(value)
                ? {}
                : item.ranges[selectedRangeIndex];
              return (
                <AnimationOnScroll
                  animateIn="animate__fadeInUp"
                  duration={0.65}
                >
                  <ListItemContainer
                    key={index}
                    label={item.label}
                    desc={description ?? ""}
                    // defaultExpanded={index === 0}
                    disabled={isNaN(value)}
                  >
                    <div className="py-3 flex items-center justify-between space-x-3 min-h-[80px]">
                      <div className="shrink-0 text-center">
                        <h4 className="font-semibold text-primary/60">
                          <span className="text-xl" style={{ color }}>
                            {isNaN(value) ? "--" : value}
                          </span>
                          {!isNaN(value) && (
                            <span className="text-sm"> {item.unit}</span>
                          )}
                        </h4>
                        {item.caregoryVisible && (
                          <p
                            className="mt-1 p-1.5 rounded text-[8px] leading-none font-medium"
                            style={{
                              color,
                              backgroundColor:
                                typeof color === "string"
                                  ? `${color}20`
                                  : undefined,
                            }}
                          >
                            {category}
                          </p>
                        )}
                      </div>
                      <Range
                        ranges={item.ranges}
                        selectedIndex={selectedRangeIndex}
                      />
                    </div>
                  </ListItemContainer>
                </AnimationOnScroll>
              );
            })}
            {(() => {
              const stressLevel = parseFloat(
                state?.result?.metadata.heart_scores.stress_index
              );
              const selectedRange = ranges.StressLevel.reduce((p, c) =>
                stressLevel > c.point ? c : p
              );
              let detailTitle = "";
              let detailDescription = "";
              if (
                !isNaN(stressLevel) &&
                (state?.result?.metadata?.heart_scores?.rmssd ?? "--") !== "--"
              ) {
                if (state.result.metadata.heart_scores.rmssd < 65) {
                  if (
                    state.result.vitals.heart_rate > 100 ||
                    state.result.vitals.bp_sys > 130 ||
                    state.result.vitals.bp_dia > 90
                  ) {
                    detailTitle = "More Sympathetic Interactions";
                    detailDescription =
                      "Although HRV manifests as a function of your heart rate, it actually originates from your nervous system. Your autonomic nervous system, which controls the involuntary aspects of your physiology, has two branches, parasympathetic (deactivating) and sympathetic (activating).\n\nThe sympathetic nervous system (often called “fight or flight”) reflects responses to things like stress and exercise, and increases your heart rate and blood pressure.";
                  } else {
                    detailTitle = "More Parasympathetic Interactions";
                    detailDescription =
                      "Although HRV manifests as a function of your heart rate, it actually originates from your nervous system. Your autonomic nervous system, which controls the involuntary aspects of your physiology, has two branches, parasympathetic (deactivating) and sympathetic (activating)\n\nThe parasympathetic nervous system (often referred to as “rest and digest”) handles inputs from internal organs, like digestion or your fingernails and hair growing.";
                  }
                } else {
                  detailTitle = "Balanced ANS Interactions";
                  detailDescription =
                    "When you have high heart rate variability, it means that your body is responsive to both sets of inputs (parasympathetic and sympathetic). This is a sign that your nervous system is balanced, and that your body is very capable of adapting to its environment and performing at its best.";
                }
              } else {
                detailDescription =
                  "Not enought data to process. This happens when the signal strength of the scan is poor due to improper light or too much movements.";
              }
              return (
                <>
                  <AnimationOnScroll
                    animateIn="animate__fadeInUp"
                    duration={0.65}
                  >
                    <div className="px-4 py-4 rounded-2xl border border-secondary/50 bg-[#fefefe] text-primary">
                      <div className="flex items-center justify-between space-x-4">
                        {!isNaN(stressLevel) && selectedRange.icon}
                        <div className="grow">
                          <h2 className="text-xs font-semibold text-primary">
                            Stress Level
                          </h2>
                          <h4
                            className="mt-1 text-xxs font-medium uppercase"
                            style={{ color: selectedRange.color }}
                          >
                            {isNaN(stressLevel) ? "--" : selectedRange.category}
                          </h4>
                        </div>
                        <div className="shrink-0 text-right font-semibold">
                          <h4
                            className="text-xl"
                            style={{ color: selectedRange.color }}
                          >
                            {isNaN(stressLevel) ? "--" : stressLevel}
                          </h4>
                          <h5 className="opacity-70 text-xxs">
                            Stress Index (SI)
                          </h5>
                        </div>
                      </div>
                      {!isNaN(stressLevel) && (
                        <div className="mt-4">
                          <h5 className="text-xs font-medium text-primary">
                            Your Stress Level is {selectedRange.category}
                          </h5>
                          <p className="mt-1.5 text-xxs font-light leading-tight whitespace-pre-line">
                            The body's reaction to a challenge or demand. There
                            are 5 levels of stress.
                          </p>
                        </div>
                      )}
                    </div>
                  </AnimationOnScroll>
                  <AnimationOnScroll
                    animateIn="animate__fadeInUp"
                    duration={0.65}
                    className="col-span-full"
                  >
                    <div className="rounded-[20px] bg-[#a8d5ff] text-primary space-y-1.5 overflow-hidden">
                      {detailTitle.length > 0 && (
                        <h3 className="text-sm font-semibold px-5 pt-4">
                          {detailTitle}
                        </h3>
                      )}
                      <p className="text-justify text-xxs font-normal leading-tight whitespace-pre-line px-5 pb-3">
                        {detailDescription}
                      </p>
                      <h5 className="text-xs mt-5 text-center font-medium px-5 h-7 flex flex-row items-center justify-center bg-[#8cc8ff]">
                        Autonomic Nervous Systems (ANS)
                      </h5>
                    </div>
                  </AnimationOnScroll>
                </>
              );
            })()}
          </section>

          <AnimationOnScroll animateIn="animate__fadeInUp" duration={0.65}>
            <section className="flex justify-between space-x-4 items-stretch mt-4">
              <div className="flex-1 flex flex-col justify-between rounded-xl overflow-hidden border border-primary/20">
                <canvas className="grow" ref={rightEyeCanvasRef} />
                <p className="px-3 py-1.5 bg-secondary text-primary text-center text-xs font-medium">
                  Right Eye
                </p>
              </div>
              <div className="flex-1 flex flex-col justify-between rounded-xl overflow-hidden border border-primary/20">
                <canvas className="grow" ref={leftEyeCanvasRef} />
                <p className="px-3 py-1.5 bg-secondary text-primary text-center text-xs font-medium">
                  Left Eye
                </p>
              </div>
            </section>
          </AnimationOnScroll>

          <section className="mb-4">
            <AnimationOnScroll animateIn="animate__fadeInUp" duration={0.65}>
              <h3 className="mb-4 text-primary mt-4 font-semibold">
                Blink Intervals
              </h3>
            </AnimationOnScroll>
            <AnimationOnScroll animateIn="animate__fadeInUp" duration={0.65}>
              {state?.eyeBlinkData?.length > 0 && (
                <BlinkIntervalsChart data={state.eyeBlinkData} />
              )}
            </AnimationOnScroll>
            <div className="mt-4 grid grid-cols-2 gap-4">
              {[
                { label: "Blinks Count", value: blinkCount ?? "--" },
                {
                  label: "Avg. Interval",
                  value: `${blinkIntervals?.avg ?? "--"} sec`,
                },
                {
                  label: "Max Interval",
                  value: `${blinkIntervals?.max ?? "--"} sec`,
                },
                {
                  label: "Min Interval",
                  value: `${blinkIntervals?.min ?? "--"} sec`,
                },
              ].map((d, idx) => (
                <AnimationOnScroll
                  animateIn="animate__fadeInUp"
                  duration={0.65}
                >
                  <p key={idx} className="text-primary text-sm font-light">
                    {d.label}:{" "}
                    <span className="text-[#4ba6fa] font-medium">
                      {d.value}
                    </span>
                  </p>
                </AnimationOnScroll>
              ))}
            </div>
          </section>
          <AnimationOnScroll animateIn="animate__fadeInUp" duration={0.65}>
            <PDFDownloadLink
              document={reportPDF}
              fileName={`VisionAI-Report-${new Date(
                state?.result?.entry_time?.replace?.(" ", "T") ?? 0
              ).toISOString()}.pdf`}
              className="flex items-center w-4/12 mx-auto justify-between space-x-4 py-3 px-5 rounded-xl bg-[#364560] text-white text-sm font-medium"
            >
              {({ loading }) => (
                <>
                  <span>
                    {loading ? "Loading document..." : "Download PDF Report"}
                  </span>
                  <Download className="flex-shrink-0 h-6 w-6" />
                </>
              )}
            </PDFDownloadLink>
          </AnimationOnScroll>
          <AnimationOnScroll animateIn="animate__fadeInUp" duration={0.65}>
            <div className="p-5 rounded-[20px] bg-[#36383d] text-black mt-4 mb-10">
              <h6 className="text-sm font-semibold text-secondary">
                For Investigational Use Only :
              </h6>
              <p className="mt-2.5 text-xxs text-white">
                Careplix VisionAI is not a medical device and should not be used
                as a substitute for clinical judgment by a health care
                professional. CarePlix VisionAI is intended to improve your
                awareness of Dry Eyes Syndrome (DES) and not intended to
                diagnose, treat, mitigate or prevent any disease, symptom,
                disorder or abnormal physical condition. Contact your
                health-care provider immediately if you suspect that you have a
                medical problem. Statements regarding clinical data have not
                been evaluated by the US FDA and are only intended for
                self-awareness and general wellness purposes only.
              </p>
            </div>
          </AnimationOnScroll>
        </div>
      </div>
    </main>
  ) : (
    <div className="fixed top-0 right-0 bottom-0 left-0 p-8 flex flex-col items-center justify-center text-center">
      <p className="text-primary/70">
        Some error occurred in
        <br />
        displaying Scan Result
      </p>
      <Link
        // @ts-ignore
        to={-3}
        className="mt-5 px-4 py-2 rounded-full bg-secondary text-white text-xs font-medium"
      >
        Retry
      </Link>
    </div>
  );
};

export default VisionResult;
